import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Tag,
  DataTable,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import type { Column } from '@dayon/design-system';
import { formatCount } from '@dayon/money';

import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';

/**
 * The second signature on a points adjustment — the half that had no door.
 *
 * `POST /api/points/adjustments` raised a request, and `approve` and `reject`
 * were both written, both correct, and both unreachable: nothing listed the
 * queue and no screen offered a button. A proposal went in and could only be
 * found again with a hand-written SELECT, while the dialog that raised it told
 * the operator "a second admin holding points.verdict has to sign this". They
 * could not, because they could not find it.
 *
 * ── WHY THE REFUSALS ARE DRAWN, NOT DISCOVERED ────────────────────────────
 *
 * Two things refuse a verdict here, and both are stated on the row before
 * anything is pressed:
 *
 *   YOU RAISED IT      `points_adjustment_maker_is_not_checker` is a database
 *                      CHECK, so a maker signing their own is refused however
 *                      the request arrives. The button says so in advance
 *                      because a control that fails on press teaches an
 *                      operator that the screen is unreliable, when in fact
 *                      the screen was right and they were not allowed.
 *
 *   NO points.verdict  The same key the redemption queue signs with. A desk
 *                      that may read this queue and not decide it is a real
 *                      desk — it is how the proposer checks on their own
 *                      request — so the rows are shown and the buttons say
 *                      which capability is missing.
 *
 * The route re-checks both. Nothing here is the guard; this is the guard
 * being legible.
 *
 * ── THE BALANCE IS THE ONE NOW ────────────────────────────────────────────
 *
 * Every row carries the player's live balance rather than the one recorded
 * when the request was raised. A DEBIT is refused at approval if the player
 * has since spent the points, so showing the figure from the moment of the
 * proposal would put a number on screen that the press is about to disagree
 * with.
 */

interface AdjustmentRow {
  id: string;
  ref: string;
  direction: 'CREDIT' | 'DEBIT';
  points: string;
  reason_code: string;
  reason_note: string | null;
  status: 'AWAITING_CHECKER' | 'APPROVED' | 'REJECTED';
  created_at: string;
  approved_at: string | null;
  rejected_reason: string | null;
  player: string;
  player_ref: string;
  raised_by: string | null;
  raised_by_id: string;
  decided_by: string | null;
  balance: string;
}

const STATUS: Record<AdjustmentRow['status'], { label: string; tone: 'accent' | 'neutral' | 'quiet' }> = {
  AWAITING_CHECKER: { label: 'Awaiting a signature', tone: 'accent' },
  APPROVED: { label: 'Approved', tone: 'neutral' },
  REJECTED: { label: 'Refused', tone: 'quiet' },
};

/** The closed set from the contract, rendered as the desk wrote it. */
const REASON_LABEL: Record<string, string> = {
  SERVICE_RECOVERY: 'Service recovery',
  PROMOTION_CORRECTION: 'Promotion correction',
  OUR_ERROR: 'Our error',
  MANUAL_AWARD: 'Manual award',
  CLAWBACK_ABUSE: 'Clawback — abuse',
  BALANCE_CORRECTION: 'Balance correction',
};

export function AdjustmentQueue({ writes, onDone }: { writes: number; onDone: () => void }) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const { allowed, principal } = useSession();

  const [rows, setRows] = useState<AdjustmentRow[]>([]);
  const [viewerId, setViewerId] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  /** The row a verdict is being given on, and which way. */
  const [deciding, setDeciding] = useState<{ row: AdjustmentRow; verdict: 'approve' | 'reject' }>();
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const canDecide = allowed('points.verdict');

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<{ items: AdjustmentRow[]; viewerId: string }>('/api/points/adjustments')
      .then((read) => {
        setRows(read.items);
        setViewerId(read.viewerId);
        setFailed(false);
      })
      .catch((error: unknown) => {
        /* Cleared with the flag: a list from the previous read sitting under a
           failure notice would be read as the current queue. */
        setRows([]);
        setFailed(true);
        showError({
          title: 'Could not read the adjustment queue',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The queue could not be read. Nothing has been changed.',
        });
      })
      .finally(() => setLoading(false));
  }, [showError]);

  useEffect(load, [load, writes]);

  /**
   * The viewer's id comes from the READ, not from the session.
   *
   * They are the same person, and the read is the one the route answered with
   * — so a session that has drifted (a re-issued token, a switched account in
   * another tab) cannot leave this screen enabling a button the API is about
   * to refuse. `principal` is the fallback for the moment before the first
   * read lands.
   */
  const me = viewerId ?? principal?.id ?? null;

  const refusal = (row: AdjustmentRow): string | null => {
    if (row.status !== 'AWAITING_CHECKER') return null;
    if (!canDecide) return 'Signing one needs points.verdict, which your role does not hold.';
    if (me !== null && row.raised_by_id === me) {
      return 'You raised this one. A proposal is signed by somebody else — the platform itself refuses the pair, not just this screen.';
    }
    return null;
  };

  const submit = async () => {
    if (!deciding) return;
    const trimmed = note.trim();
    if (deciding.verdict === 'reject' && trimmed.length < 10) {
      setNoteError('Say why it is refused — at least 10 characters. The proposer is owed a reason.');
      return;
    }
    setNoteError(undefined);
    setBusy(true);
    try {
      await api.post(
        `/api/points/adjustments/${deciding.row.id}/${deciding.verdict}`,
        deciding.verdict === 'reject' ? { note: trimmed } : trimmed ? { note: trimmed } : {},
      );
      toast(
        deciding.verdict === 'approve'
          ? `${deciding.row.ref} approved — ${formatCount(Number(deciding.row.points))} points ${
              deciding.row.direction === 'CREDIT' ? 'credited to' : 'taken from'
            } ${deciding.row.player_ref}.`
          : `${deciding.row.ref} refused. No points moved.`,
      );
      setDeciding(undefined);
      setNote('');
      load();
      onDone();
    } catch (error: unknown) {
      showError({
        title: deciding.verdict === 'approve' ? 'Not approved' : 'Not refused',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was decided and no points moved.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  const waiting = rows.filter((r) => r.status === 'AWAITING_CHECKER').length;

  const columns: Column<AdjustmentRow>[] = [
    {
      key: 'ref',
      header: 'Request',
      required: true,
      render: (r) => <span className="tabular">{r.ref}</span>,
    },
    {
      key: 'player',
      header: 'Player',
      required: true,
      render: (r) => (
        <>
          {r.player}
          <div className="cell-sub tabular">{r.player_ref}</div>
        </>
      ),
    },
    {
      key: 'movement',
      header: 'Movement',
      numeric: true,
      render: (r) => (
        <>
          <strong>
            {r.direction === 'CREDIT' ? '+' : '\u2212'}
            {formatCount(Number(r.points))}
          </strong>
          <div className="cell-sub">{r.direction === 'CREDIT' ? 'Credit' : 'Debit'}</div>
        </>
      ),
    },
    {
      key: 'balance',
      header: 'Points balance',
      numeric: true,
      secondary: true,
      render: (r) => <span className="tabular">{formatCount(Number(r.balance))}</span>,
    },
    {
      key: 'why',
      header: 'Why',
      secondary: true,
      render: (r) => (
        <>
          {REASON_LABEL[r.reason_code] ?? r.reason_code}
          {r.reason_note ? <div className="cell-sub">{r.reason_note}</div> : null}
        </>
      ),
    },
    {
      key: 'raised',
      header: 'Raised by',
      secondary: true,
      render: (r) => r.raised_by ?? '\u2014',
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <>
          <Tag tone={STATUS[r.status].tone}>{STATUS[r.status].label}</Tag>
          {r.status === 'REJECTED' && r.rejected_reason ? (
            <div className="cell-sub">{r.rejected_reason}</div>
          ) : null}
          {r.status !== 'AWAITING_CHECKER' && r.decided_by ? (
            <div className="cell-sub">Decided by {r.decided_by}</div>
          ) : null}
        </>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (r) => {
        if (r.status !== 'AWAITING_CHECKER') return '\u2014';
        const blocked = refusal(r);
        /* THE REASON IS THE CONTROL. A dead button beside a sentence nobody
           reads is how an operator concludes the console is broken; here the
           sentence replaces the button. */
        if (blocked) return <span className="cell-sub">{blocked}</span>;
        return (
          /* `small`, like every other action button in a table on this console
             — the redemption queue beside it and the rails table both do. At
             full size these two were taller than the row they sit in and pushed
             it out of line with the ones above and below. */
          <div className="row-actions">
            <Button
              small
              variant="secondary"
              onClick={() => {
                setNote('');
                setNoteError(undefined);
                setDeciding({ row: r, verdict: 'reject' });
              }}
            >
              Refuse
            </Button>
            <Button
              small
              variant="primary"
              onClick={() => {
                setNote('');
                setNoteError(undefined);
                setDeciding({ row: r, verdict: 'approve' });
              }}
            >
              Approve
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          A proposal on this queue has moved <strong>nothing</strong>. The points are still exactly
          where they were when it was raised, and approving is the moment they move — the ledger
          entry, the request and the audit entry are saved together, all of them or none. Refusing
          moves nothing either and records why. <strong>Whoever raised a request may not decide
          it</strong>: the platform&rsquo;s own records refuse it, not just this screen being
          careful, so it holds however the request arrives. A debit is checked against the balance shown here at the moment it
          is approved — a player who has spent the points since it was raised has spent them, and
          the approval is refused rather than taking the account below zero.
        </div>
      </div>

      {failed ? (
        <EmptyState icon="warning" title="The queue could not be read">
          Nothing has been changed. The figures above do not count adjustments.
        </EmptyState>
      ) : (
        <DataTable<AdjustmentRow>
          caption={`Points adjustments — ${formatCount(waiting)} awaiting a signature, waiting first`}
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          empty={
            /* ── THREE DIFFERENT EMPTY TABLES, AND THE WORDS ARE THE ONLY TELL ────
               A table with no rows in it means three unrelated things on this
               screen, and an operator has nothing but the sentence to tell them
               apart: the read failed (the warning above this table, which says
               so and says that nothing has been changed), the read has not come
               back yet (below), or the read came back and the queue is honestly
               empty (below that). Collapsing any two of those into a limp
               "nothing to show" is how somebody either chases a request that was
               never raised or trusts a queue that was never fetched, so each
               state goes on saying which one it is.

               The honest-zero wording also makes two promises about the read
               behind it, and both are kept. `GET /api/points/adjustments` selects
               straight from points.points_adjustment on every mount — and again
               each time `writes` ticks — so there is no cache in front of it to
               go stale; and this screen sends no status, so approved and refused
               requests would be sitting here too if any existed. What the read IS
               narrowed by is the brand scope, which is why the sentence says "for
               the brands you can see" rather than claiming the whole platform is
               empty: a brand-scoped desk is shown its own brands' requests and
               nobody else's. The table name stays in this comment, where whoever
               maintains that read will want it, instead of in front of the desk
               signing the requests. */
            loading ? (
              <EmptyState icon="clock" title="Reading the queue…">
                One moment.
              </EmptyState>
            ) : (
              <EmptyState icon="check" title="No adjustments have been raised">
                A points adjustment is proposed with the button above this table, and signed here by
                somebody else. This is a real zero and not a read that failed: the queue was read a
                moment ago, fresh rather than from a cache, and nothing has been raised yet for the
                brands you can see.
              </EmptyState>
            )
          }
        />
      )}

      {/*
        NOT DISMISSIBLE, AND THE GUARD BELONGS ON THIS SIDE OF THE BUTTON.

        One dialog gives both verdicts, and either way an Escape struck by
        accident throws away a decision somebody opened this queue to make —
        along with the reason they had already typed, which on a refusal is the
        only thing the person who raised the request will ever read. Nothing
        says it happened: the dialog closes exactly as it does when Cancel is
        pressed, the row goes back to awaiting a signature, and the operator is
        left to work out whether they decided it or not.

        So this is `false` outright rather than `{!busy}`. A guard that only
        holds while the request is in flight covers the one moment when the
        verdict has already been given — the press cannot be recalled by then
        anyway — and leaves the moment that actually matters, before the press,
        wide open. Cancel sits an inch away and names itself, so there is no
        exit this removes.
      */}
      <Dialog
        open={deciding !== undefined}
        onClose={() => setDeciding(undefined)}
        dismissible={false}
        title={
          deciding === undefined
            ? ''
            : deciding.verdict === 'approve'
              ? `Approve ${deciding.row.ref}?`
              : `Refuse ${deciding.row.ref}?`
        }
        actions={
          <>
            <Button variant="ghost" onClick={() => setDeciding(undefined)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void submit()} disabled={busy}>
              {deciding?.verdict === 'approve' ? 'Approve and post it' : 'Refuse it'}
            </Button>
          </>
        }
      >
        {deciding === undefined ? null : (
          <div className="stack">
            {/*
              TWO TONES OUT OF ONE DIALOG, because the two verdicts are not the
              same kind of act and the colour is the first thing anybody reads.

              APPROVING IS `notice-danger`. The movement is posted on the press:
              the player's balance changes there and then, and points are spent
              on rewards the operator hands over as cash, as a bonus or as goods
              — so this is a figure that is reported and spendable, not a label
              on a record. Nothing on this platform takes an approval back. A
              request is decided exactly once and a decided one is refused a
              second verdict, so the only way back is a fresh request in the
              other direction, which needs two people all over again.

              REFUSING IS `notice-warn`, and deliberately not danger. Not one
              point moves, nothing is posted, and what closes is a PROPOSAL: if
              it should have gone through, it can be raised again. Dressing that
              in the same red as the press that changes somebody's balance is
              how red stops meaning anything — and the other half of this very
              dialog is the press that needs it to still mean something.

              THE PROSE IS THE PARAGRAPH THIS BLOCK ALREADY CARRIED, kept word
              for word, with the halves an operator would otherwise discover
              afterwards added to each branch: that an approved movement turns
              up in the player's own points history under this request's
              reference, and that a refusal reaches nobody but the person who
              raised it. Both are checked rather than assumed — the player's
              points screen lists every movement with its reference, and no mail
              is sent on either verdict, which is why the approve branch says
              the player is not emailed instead of leaving it to be guessed.
            */}
            <div
              className={
                deciding.verdict === 'approve' ? 'notice notice-danger' : 'notice notice-warn'
              }
            >
              <Icon name="warning" />
              <div>
                {deciding.verdict === 'approve' ? (
                  <>
                    <strong>
                      {formatCount(Number(deciding.row.points))} points{' '}
                      {deciding.row.direction === 'CREDIT' ? 'to' : 'from'} {deciding.row.player}
                    </strong>{' '}
                    ({deciding.row.player_ref}), who holds{' '}
                    {formatCount(Number(deciding.row.balance))} right now. This posts the ledger row
                    — the points move when you press it, and there is no step after this one.
                    {deciding.row.direction === 'DEBIT'
                      ? ' A debit that would take the balance below zero is refused instead.'
                      : ''}{' '}
                    The player is not emailed, but the movement appears in their own points history
                    with this request's reference beside it. An approval cannot be taken back: if it
                    turns out to be wrong, putting it right means raising a fresh request the other
                    way, signed by a second person again.
                  </>
                ) : (
                  <>
                    Nothing moves. The request is closed against{' '}
                    <strong>{deciding.row.player}</strong> ({deciding.row.player_ref}) and your
                    reason is kept with it — the person who raised it reads this and nothing else.
                    The player is not told and nothing shows in their points history, because
                    nothing moved. A request is decided once, so this one cannot be reopened: if it
                    should go through after all, it has to be raised again.
                  </>
                )}
              </div>
            </div>
            <Field
              label={deciding.verdict === 'approve' ? 'Note (optional)' : 'Why it is refused'}
              error={noteError}
              hint={
                deciding.verdict === 'approve'
                  ? 'Kept with the request and in the audit trail. Nothing is being refused, so nothing has to be explained.'
                  : 'At least 10 characters. A code on its own reads as a form number.'
              }
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={
                    deciding.verdict === 'approve'
                      ? 'Checked against ticket TK-00412.'
                      : 'The bet settled correctly; the points were already awarded on 12 Sep.'
                  }
                />
              )}
            </Field>
          </div>
        )}
      </Dialog>
    </>
  );
}
